import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiChevronLeft, FiChevronRight, FiCreditCard, FiLogOut, FiSettings, FiShield, FiUser, FiX } from 'react-icons/fi';

export function useNotice(initial = null) {
  const [notice, setValue] = useState(initial);
  const setNotice = useCallback((value) => setValue(value
    ? typeof value === 'string' ? { type: 'error', message: value } : value
    : null), []);
  useEffect(() => {
    if (notice?.type !== 'success') return undefined;
    const timer = setTimeout(() => setValue((current) => current === notice ? null : current), 3000);
    return () => clearTimeout(timer);
  }, [notice]);
  return [notice, setNotice];
}

export function Toast({ notice, onDismiss, dark = false }) {
  if (!notice?.message) return null;
  return createPortal(<div className={`dashboard-toast ${notice.type || 'neutral'}${dark ? ' dark' : ''}`} role={notice.type === 'error' ? 'alert' : 'status'} aria-atomic="true">
    <span>{notice.message}</span><button type="button" aria-label="Dismiss notification" title="Dismiss notification" onClick={() => onDismiss(null)}><FiX /></button>
  </div>, document.body);
}

export function Skeleton({ rows = 3, label = 'Loading' }) {
  return <div className="loading-skeleton" role="status" aria-label={label}>
    {Array.from({ length: rows }, (_, index) => <span key={index} />)}
  </div>;
}

export function ProfileMenu({ user, onSettings, onProfile, onSecurity, onLogout, onTour, onBilling }) {
  const [open, setOpen] = useState(false);
  const root = useRef(null);
  useEffect(() => {
    const close = (event) => { if (!root.current?.contains(event.target) || event.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', close);
    document.addEventListener('keydown', close);
    return () => { document.removeEventListener('pointerdown', close); document.removeEventListener('keydown', close); };
  }, []);
  return <div className="header-profile" ref={root}>
    <button className="header-avatar" type="button" title="My account" aria-label="My account" aria-expanded={open} onClick={() => setOpen(!open)}>
      {user.profileImageUrl ? <img src={user.profileImageUrl} alt="" /> : String(user.name || 'Account').slice(0, 2).toUpperCase()}
    </button>
    {open && <div className="header-profile-menu"><strong>{user.name || 'My account'}</strong>
      {onProfile && <button type="button" onClick={() => { setOpen(false); onProfile(); }}><FiUser />My Profile</button>}
      {onSecurity && <button type="button" onClick={() => { setOpen(false); onSecurity(); }}><FiShield />Security</button>}
      {onSettings && <button type="button" onClick={() => { setOpen(false); onSettings(); }}><FiSettings />Settings</button>}
      {onBilling && <button type="button" onClick={() => { setOpen(false); onBilling(); }}><FiCreditCard />Plan &amp; Payments</button>}
      {onTour && <button type="button" onClick={() => { setOpen(false); onTour(); }}><FiChevronRight />Getting started</button>}
      <button type="button" onClick={onLogout}><FiLogOut />Logout</button>
    </div>}
  </div>;
}

export function Onboarding({ userId, onNavigate, onClose }) {
  const [step, setStep] = useState(0);
  const closeButton = useRef(null);
  const steps = [ ['Workspace details', 'workspace'], ['Connected profiles', 'profiles'],
    ['Media library', 'post'], ['AI writing studio', 'post'], ['Publishing destinations', 'post'],
    ['Content calendar', 'calendar'], ['Reports and performance', 'reports'] ];
  useEffect(() => { closeButton.current?.focus(); }, []);
  const finish = () => { localStorage.setItem(`cosocial-onboarding-${userId}`, 'done'); onClose(); };
  return <section className="onboarding-panel" role="dialog" aria-modal="false" aria-labelledby="onboarding-title" onKeyDown={(event) => { if (event.key === 'Escape') finish(); }}>
    <header><span>Getting started · {step + 1} / {steps.length}</span><button ref={closeButton} aria-label="Close getting started" type="button" onClick={finish}><FiX /></button></header>
    <h2 id="onboarding-title">{steps[step][0]}</h2>
    <div className="onboarding-progress">{steps.map(([name], index) => <i className={index <= step ? 'active' : ''} key={name} />)}</div>
    <footer><button type="button" aria-label="Previous step" disabled={!step} onClick={() => setStep(step - 1)}><FiChevronLeft /></button>
      <button className="btn primary" type="button" onClick={() => { onNavigate(steps[step][1]); if (step === steps.length - 1) finish(); else setStep(step + 1); }}>Open {steps[step][0]}<FiChevronRight /></button>
    </footer>
  </section>;
}

export function ActivityChart({ series, days }) {
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...series.flatMap((item) => item.values));
  const ticks = [0, 0.25, 0.5, 0.75, 1];
  const buckets = series[0]?.values.length || 7;
  const dateLabel = (index) => new Date(Date.now() - days * 86400000 * (1 - index / buckets)).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  return <div className="activity-chart" onMouseLeave={() => setHover(null)}>
    <div className="chart-hover-readout" role="status">{hover ? <><strong>{hover.label}</strong><span>{hover.date}</span><b>{hover.value.toLocaleString('en-IN')}</b></> : <span>Selected period · {days} days</span>}</div>
    <svg viewBox="0 0 700 270" role="img" aria-label={`Activity for the last ${days} days`}>
      {ticks.map((tick) => <g key={tick}><line className="grid-line" x1="48" x2="690" y1={224 - tick * 192} y2={224 - tick * 192} /><text x="39" y={228 - tick * 192} textAnchor="end">{Math.round(max * tick).toLocaleString('en-IN')}</text></g>)}
      {series.map((item, seriesIndex) => item.values.map((value, index) => {
        const groupWidth = 640 / buckets;
        const width = (groupWidth - 18) / Math.max(series.length, 1);
        const detail = { label: item.label, value, date: `${dateLabel(index)} - ${dateLabel(index + 1)}` };
        return <rect tabIndex="0" role="graphics-symbol" aria-label={`${detail.label}, ${detail.date}: ${value}`} className="chart-bar" key={`${item.id}-${index}`}
          x={50 + index * groupWidth + seriesIndex * width + 6} y={224 - value / max * 192} width={Math.max(1, width - 4)} height={value / max * 192} rx="3" fill={item.color}
          onMouseEnter={() => setHover(detail)} onFocus={() => setHover(detail)} onBlur={() => setHover(null)}><title>{`${detail.date}: ${value} ${item.label}`}</title></rect>;
      }))}
      {[0, Math.floor(buckets / 2), buckets].map((index) => <text key={index} x={50 + index * (640 / buckets)} y="254" textAnchor={index === 0 ? 'start' : index === buckets ? 'end' : 'middle'}>{dateLabel(index)}</text>)}
    </svg>
  </div>;
}
